import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "operator_os_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const login = async (password) => {
  const { data } = await axios.post(`${API}/auth/login`, { password });
  return data;
};

export const verifyToken = async () => {
  try {
    await client.get("/auth/verify");
    return true;
  } catch {
    return false;
  }
};

export const api = {
  top3: () => client.get("/warroom/top3").then((r) => r.data),
  patchTop3: (id, patch) => client.patch(`/warroom/top3/${id}`, patch).then((r) => r.data),
  toggleSubtask: (id, subId, done) =>
    client.patch(`/warroom/top3/${id}/subtask/${subId}`, { done }).then((r) => r.data),

  moneyMoves: () => client.get("/warroom/money-moves").then((r) => r.data),
  patchMoneyMove: (id, patch) => client.patch(`/warroom/money-moves/${id}`, patch).then((r) => r.data),
  createMoneyMove: (body = {}) => client.post("/warroom/money-moves", body).then((r) => r.data),
  deleteMoneyMove: (id) => client.delete(`/warroom/money-moves/${id}`).then((r) => r.data),
  reorderMoneyMoves: (ids) => client.post("/warroom/money-moves/reorder", { ids }).then((r) => r.data),

  doNotDrop: () => client.get("/warroom/do-not-drop").then((r) => r.data),
  patchDND: (id, patch) => client.patch(`/warroom/do-not-drop/${id}`, patch).then((r) => r.data),
  createDND: (body = {}) => client.post("/warroom/do-not-drop", body).then((r) => r.data),
  deleteDND: (id) => client.delete(`/warroom/do-not-drop/${id}`).then((r) => r.data),
  reorderDND: (ids) => client.post("/warroom/do-not-drop/reorder", { ids }).then((r) => r.data),

  timeline: () => client.get("/warroom/timeline").then((r) => r.data),
  patchTimeline: (id, patch) => client.patch(`/warroom/timeline/${id}`, patch).then((r) => r.data),
  createTimeline: (body = {}) => client.post("/warroom/timeline", body).then((r) => r.data),
  deleteTimeline: (id) => client.delete(`/warroom/timeline/${id}`).then((r) => r.data),
  reorderTimeline: (ids) => client.post("/warroom/timeline/reorder", { ids }).then((r) => r.data),
  pulse: () => client.get("/warroom/business-pulse").then((r) => r.data),
  patchPulse: (id, patch) => client.patch(`/warroom/business-pulse/${id}`, patch).then((r) => r.data),
  waitingOn: () => client.get("/warroom/waiting-on").then((r) => r.data),
  patchWaiting: (id, patch) => client.patch(`/warroom/waiting-on/${id}`, patch).then((r) => r.data),
  createWaiting: (body = {}) => client.post("/warroom/waiting-on", body).then((r) => r.data),
  deleteWaiting: (id) => client.delete(`/warroom/waiting-on/${id}`).then((r) => r.data),
  reorderWaiting: (ids) => client.post("/warroom/waiting-on/reorder", { ids }).then((r) => r.data),

  createCapture: (body) => client.post("/captures", body).then((r) => r.data),
  prepareCapture: (raw_text, business) =>
    client.post("/captures/prepare", { raw_text, business }).then((r) => r.data),
  commitCapture: (body) => client.post("/captures/commit", body).then((r) => r.data),
  voiceCapture: (formData) =>
    client.post("/voice/capture", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data),
  listCaptures: () => client.get("/captures").then((r) => r.data),

  postEod: (body) => client.post("/end-of-day", body).then((r) => r.data),
  listEod: () => client.get("/end-of-day").then((r) => r.data),

  business: (slug) => client.get(`/business/${slug}`).then((r) => r.data),
  patchBusiness: (slug, patch) => client.patch(`/business/${slug}`, patch).then((r) => r.data),

  todoistStatus: () => client.get("/todoist/status").then((r) => r.data),
  todoistConnect: (token) => client.put("/todoist/token", { token }).then((r) => r.data),
  todoistDisconnect: () => client.delete("/todoist/token").then((r) => r.data),
  todoistProjects: () => client.get("/todoist/projects").then((r) => r.data),
  todoistTasks: (filter = "today") =>
    client.get("/todoist/tasks", { params: { filter } }).then((r) => r.data),
  todoistTasksByProject: (project_id) =>
    client.get("/todoist/tasks", { params: { filter: "", project_id } }).then((r) => r.data),
  todoistRouting: () => client.get("/todoist/routing").then((r) => r.data),
  setTodoistRouting: (body) => client.put("/todoist/routing", body).then((r) => r.data),
  todoistCounts: () => client.get("/todoist/counts").then((r) => r.data),
  closeTodoistTask: (task_id) =>
    client.post(`/todoist/tasks/${task_id}/close`).then((r) => r.data),
  reopenTodoistTask: (task_id) =>
    client.post(`/todoist/tasks/${task_id}/reopen`).then((r) => r.data),
  updateTodoistTask: (task_id, content) =>
    client.post(`/todoist/tasks/${task_id}/update`, { content }).then((r) => r.data),

  // Live Todoist-driven War Room data
  dueTodayCount: () => client.get("/warroom/due-today-count").then((r) => r.data),
  waitingTodoist: () => client.get("/warroom/waiting").then((r) => r.data),
  reorderTop3: (ids) => client.post("/warroom/top3/reorder", { ids }).then((r) => r.data),
  reorderWaitingTodoist: (ids) => client.post("/warroom/waiting/reorder", { ids }).then((r) => r.data),
  syncTop3Todoist: (id) => client.post(`/warroom/top3/${id}/sync-todoist`).then((r) => r.data),
  promoteToTop3: (body) => client.post("/warroom/top3/promote", body).then((r) => r.data),

  // Money
  money: () => client.get("/money").then((r) => r.data),
  patchMoney: (id, body) => client.patch(`/money/${id}`, body).then((r) => r.data),
  createMoney: (body) => client.post("/money", body).then((r) => r.data),
  deleteMoney: (id) => client.delete(`/money/${id}`).then((r) => r.data),

  // Waiting On (v2)
  waitingV2: () => client.get("/waiting-on-v2").then((r) => r.data),
  patchWaitingV2: (id, body) => client.patch(`/waiting-on-v2/${id}`, body).then((r) => r.data),
  createWaitingV2: (body) => client.post("/waiting-on-v2", body).then((r) => r.data),
  deleteWaitingV2: (id) => client.delete(`/waiting-on-v2/${id}`).then((r) => r.data),

  // Morning brief
  latestBrief: () => client.get("/morning-brief/latest").then((r) => r.data),
  generateBrief: () => client.post("/morning-brief/generate").then((r) => r.data),

  // Assistant
  assistantChat: (body) => client.post("/assistant/chat", body).then((r) => r.data),
  assistantVoice: (formData) =>
    client.post("/assistant/voice", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data),
  assistantTTS: (text, voice) =>
    client.post("/assistant/tts", { text, voice }, { responseType: "blob" }).then((r) => r.data),
  assistantMemory: () => client.get("/assistant/memory").then((r) => r.data),
  addAssistantMemory: (body) => client.post("/assistant/memory", body).then((r) => r.data),
  deleteAssistantMemory: (id) => client.delete(`/assistant/memory/${id}`).then((r) => r.data),
  assistantHistory: () => client.get("/assistant/history").then((r) => r.data),
  deleteAssistantSession: (sid) => client.delete(`/assistant/sessions/${sid}`).then((r) => r.data),

  // Reports
  reportKinds: () => client.get("/reports/kinds").then((r) => r.data),
  latestReport: (kind, business) =>
    client.get("/reports/latest", { params: { kind, ...(business ? { business } : {}) } }).then((r) => r.data),
  generateReport: (kind, business) =>
    client.post("/reports/generate", null, { params: { kind, ...(business ? { business } : {}) } }).then((r) => r.data),

  // Money candidates (from Todoist)
  scanMoneyCandidates: () => client.post("/money/candidates/scan").then((r) => r.data),
  moneyCandidates: () => client.get("/money/candidates").then((r) => r.data),
  confirmMoneyCandidate: (id, body) => client.post(`/money/candidates/${id}/confirm`, body).then((r) => r.data),
  dismissMoneyCandidate: (id) => client.post(`/money/candidates/${id}/dismiss`).then((r) => r.data),
};
